import { useEffect } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import Card from '@mui/material/Card';
import TableRow from '@mui/material/TableRow';
import { Box, Button, Container, IconButton } from '@mui/material';
import { Link, Link as RouterLink } from 'react-router-dom';
import Page from '../../components/Page';
import { useAppSelector, useAppDispatch } from '../../store';
import {
  setLimit,
  setSkip,
  setCurrentPage,
} from '../../store/settings/settingsSlice';
import { findSettings } from '../../store/settings/api';
import Loader from '../../components/Loader';
import Label from '../../components/Label';
import Iconify from '../../components/Iconify';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import Scrollbar from '../../components/Scrollbar';
import { setStatus as setSettingsStatus } from '../../store/settings/settingsSlice';
import { encryptString } from '../../utils/url';

const Index: React.FC = () => {
  const dispatch = useAppDispatch();
  const { data, totalRecords, limit, currentPage, skip, isLoading } =
    useAppSelector((state) => state.settings);

  useEffect(() => {
    dispatch(
      findSettings({
        limit,
        skip,
        search: 'faq',
      })
    );
  }, [dispatch, limit, skip]);

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
    dispatch(setSettingsStatus({ status: 'idle' }));
  };
  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
  };

  return (
    <>
      <Page title={'FAQ: List'}>
        <Container maxWidth={'xl'}>
          <HeaderBreadcrumbs
            heading={'FAQ'}
            links={[{ name: 'Dashboard', href: '/' }]}
            action={
              <Button
                variant='contained'
                component={RouterLink}
                to={'/faq/create'}
                startIcon={<Iconify icon={'eva:plus-fill'} />}
              >
                {'New FAQ'}
              </Button>
            }
          />

          <Card>
            <Scrollbar>
              <TableContainer
                sx={{ minWidth: 1000, position: 'relative', pt: 2 }}
              >
                <Table size={'medium'}>
                  <TableHead>
                    <TableRow>
                      <TableCell align='center'>{'Name'}</TableCell>
                      <TableCell align='center'>{'Type'}</TableCell>
                      <TableCell align='center'>{'Language'}</TableCell>
                      <TableCell align='center'>{'Status'}</TableCell>
                      <TableCell align='center'>{'Action'}</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {isLoading ? (
                      <TableRow hover>
                        <TableCell
                          colSpan={7}
                          align='center'
                        >
                          <Loader />
                        </TableCell>
                      </TableRow>
                    ) : data?.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          align='center'
                        >
                          {'No record found'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      data?.map((settings) => (
                        <TableRow key={settings.id}>
                          <TableCell align='center'>{settings?.name}</TableCell>
                          <TableCell align='center'>
                            {settings?.valueType}
                          </TableCell>
                          <TableCell align='center'>
                            {settings?.language}
                          </TableCell>
                          <TableCell align='center'>
                            <Label
                              variant='ghost'
                              color={settings.activeState ? 'success' : 'error'}
                            >
                              {settings.activeState ? 'Active' : 'InActive'}
                            </Label>
                          </TableCell>
                          <TableCell align='center'>
                            <IconButton aria-label='visibility'>
                              <Link
                                to={`/faq/edit/${encryptString(
                                  settings?.id ?? ''
                                )}`}
                                state={{ ...settings }}
                                style={{ color: '#808080' }}
                              >
                                <Iconify icon={'eva:edit-fill'} />
                              </Link>
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>

            <Box sx={{ position: 'relative' }}>
              <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component='div'
                count={totalRecords}
                rowsPerPage={limit}
                page={Math.min(Math.round(totalRecords / limit), currentPage)}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </Box>
          </Card>
        </Container>
      </Page>
    </>
  );
};

export default Index;
